import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Bell, BellRing, CloudRain, Snowflake, Fish, Crosshair,
  Waves, AlertTriangle, Check, CheckCheck, Trash2, Settings,
  X, Volume2, VolumeX
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  "cloud-rain": CloudRain,
  "snowflake": Snowflake,
  "fish": Fish,
  "crosshair": Crosshair,
  "waves": Waves,
  "alert-triangle": AlertTriangle,
  "bell": Bell,
};

const COLOR_MAP = {
  rain_soon: "text-blue-400",
  frost_warning: "text-cyan-300",
  fishing_window: "text-emerald-400",
  hunting_window: "text-amber-400",
  lake_level_change: "text-blue-300",
  severe_weather: "text-red-400",
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function NotificationBell({ token, isDarkMode }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [checking, setChecking] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 20 }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Notification fetch error:", err.message);
    }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/notifications/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data.settings);
      setPushEnabled(Boolean(res.data.settings?.push_enabled));
    } catch (err) {
      console.error("Settings fetch error:", err.message);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchSettings]);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
        setShowSettings(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("Mark read error:", err.message);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Mark all read error:", err.message);
    }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${API}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Delete notification error:", err.message);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/api/notifications/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = res.data.alerts_generated || 0;
      if (count > 0) {
        toast.success(`${count} new alert${count > 1 ? "s" : ""} generated`);
        await fetchNotifications();
      } else {
        toast.info("No new alerts right now");
      }
    } catch (err) {
      toast.error("Failed to check alerts");
    } finally {
      setChecking(false);
    }
  };

  const togglePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Push notifications not supported in this browser");
      return;
    }
    try {
      if (pushEnabled) {
        await axios.delete(`${API}/api/notifications/push/unsubscribe`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPushEnabled(false);
        toast.info("Push notifications disabled");
      } else {
        if ("Notification" in window && Notification.permission !== "granted") {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") {
            toast.error("Push notifications permission was not granted");
            return;
          }
        }

        const keyRes = await axios.get(`${API}/api/notifications/vapid-key`);
        const vapidKey = keyRes.data?.publicKey;
        if (!vapidKey) {
          toast.error("Push notifications are not configured yet");
          return;
        }

        const reg = await navigator.serviceWorker.register("/sw-push.js", { scope: "/" });
        const existingSub = await reg.pushManager.getSubscription();
        if (existingSub) await existingSub.unsubscribe();
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        await axios.post(`${API}/api/notifications/push/subscribe`, {
          subscription: sub.toJSON()
        }, { headers: { Authorization: `Bearer ${token}` } });
        setPushEnabled(true);
        toast.success("Push notifications enabled!");
      }
    } catch (err) {
      console.error("Push toggle error:", err);
      toast.error("Failed to toggle push notifications");
    }
  };

  const updateAlertSetting = async (alertType, enabled) => {
    const newAlerts = { ...settings.alerts, [alertType]: enabled };
    setSettings((s) => ({ ...s, alerts: newAlerts }));
    try {
      await axios.put(`${API}/api/notifications/settings`, {
        ...settings, alerts: newAlerts
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      console.error("Settings update error:", err.message);
    }
  };

  const BellIcon = unreadCount > 0 ? BellRing : Bell;

  return (
    <div className="relative w-full sm:w-auto" ref={panelRef}>
      {/* Bell Button */}
      <button
        data-testid="notification-bell"
        onClick={() => { setOpen(!open); setShowSettings(false); }}
        className="relative flex h-10 w-full items-center justify-center rounded-xl hover:bg-white/10 transition-colors sm:w-auto sm:p-2"
      >
        <BellIcon className={`w-5 h-5 ${unreadCount > 0 ? "text-[#00f2ea]" : "text-white/60"}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            data-testid="notification-panel"
            className={`fixed left-3 right-3 top-28 max-h-[calc(100vh-8rem)] rounded-2xl border shadow-2xl z-50 overflow-hidden sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[380px] sm:max-h-[520px] ${
              isDarkMode ? "bg-[#111827] border-white/10" : "bg-white border-gray-200"
            }`}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? "border-white/5" : "border-gray-100"}`}>
              <h3 className="font-bold text-sm">
                {showSettings ? "Alert Settings" : "Notifications"}
              </h3>
              <div className="flex items-center gap-1">
                {!showSettings && (
                  <>
                    <button onClick={checkNow} disabled={checking}
                      className={`p-1.5 rounded-lg text-xs font-semibold ${checking ? "opacity-50" : "hover:bg-white/10"} text-[#00f2ea]`}
                      data-testid="check-alerts-btn">
                      {checking ? "Checking..." : "Check Now"}
                    </button>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="p-1.5 rounded-lg hover:bg-white/10" title="Mark all read" data-testid="notifications-mark-all-read-button">
                        <CheckCheck className="w-4 h-4 text-white/40" />
                      </button>
                    )}
                  </>
                )}
                <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="notification-settings-toggle-button">
                  {showSettings ? <X className="w-4 h-4 text-white/40" /> : <Settings className="w-4 h-4 text-white/40" />}
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && settings ? (
              <div className="p-4 space-y-3 overflow-y-auto max-h-[420px]">
                {/* Push toggle */}
                <div className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? "bg-white/5" : "bg-gray-50"}`}>
                  <div className="flex items-center gap-2">
                    {pushEnabled ? <Volume2 className="w-4 h-4 text-[#00f2ea]" /> : <VolumeX className="w-4 h-4 text-white/40" />}
                    <span className="text-sm font-medium">Browser Push</span>
                  </div>
                  <button onClick={togglePush}
                    data-testid="push-toggle"
                    className={`w-10 h-6 rounded-full transition-colors ${pushEnabled ? "bg-[#00f2ea]" : isDarkMode ? "bg-white/20" : "bg-gray-300"}`}>
                    <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${pushEnabled ? "translate-x-4" : ""}`} />
                  </button>
                </div>

                {/* Alert type toggles */}
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>Alert Types</p>
                {Object.entries(settings.alerts || {}).map(([key, enabled]) => {
                  const info = { rain_soon: "Rain Starting Soon", frost_warning: "Frost/Freeze Warning", fishing_window: "Perfect Fishing Window", hunting_window: "Perfect Hunting Window", lake_level_change: "Lake Level Change", severe_weather: "Severe Weather" };
                  const IconComp = ICON_MAP[({ rain_soon: "cloud-rain", frost_warning: "snowflake", fishing_window: "fish", hunting_window: "crosshair", lake_level_change: "waves", severe_weather: "alert-triangle" })[key]] || Bell;
                  return (
                    <div key={`alert-setting-${key}`} className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? "bg-white/5" : "bg-gray-50"}`}>
                      <div className="flex items-center gap-2">
                        <IconComp className={`w-4 h-4 ${COLOR_MAP[key] || "text-white/40"}`} />
                        <span className="text-sm">{info[key] || key}</span>
                      </div>
                      <button onClick={() => updateAlertSetting(key, !enabled)}
                        data-testid={`toggle-${key}`}
                        className={`w-10 h-6 rounded-full transition-colors ${enabled ? "bg-[#00f2ea]" : isDarkMode ? "bg-white/20" : "bg-gray-300"}`}>
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${enabled ? "translate-x-4" : ""}`} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Notifications List */
              <div className="overflow-y-auto max-h-[420px]">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <Bell className="w-10 h-10 text-white/10 mb-3" />
                    <p className={`text-sm ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>No notifications yet</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? "text-white/20" : "text-gray-300"}`}>Tap Check Now to scan weather conditions</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const IconComp = ICON_MAP[n.icon] || Bell;
                    return (
                      <div
                        key={`notif-${n.id}`}
                        data-testid={`notification-${n.id}`}
                        className={`flex items-start gap-3 px-4 py-3 border-b transition-colors cursor-pointer group ${
                          isDarkMode
                            ? `border-white/5 ${n.read ? "opacity-60" : "bg-white/[0.02]"}`
                            : `border-gray-50 ${n.read ? "opacity-60" : "bg-blue-50/30"}`
                        } hover:bg-white/5`}
                        onClick={() => !n.read && markRead(n.id)}
                      >
                        <div className={`p-2 rounded-xl ${isDarkMode ? "bg-white/5" : "bg-gray-100"} mt-0.5`}>
                          <IconComp className={`w-4 h-4 ${COLOR_MAP[n.type] || "text-white/40"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wide text-[#00f2ea]">{n.title}</span>
                            {!n.read && <span className="w-2 h-2 bg-[#00f2ea] rounded-full flex-shrink-0" />}
                          </div>
                          <p className={`text-sm mt-0.5 leading-snug ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{n.message}</p>
                          <span className={`text-[10px] mt-1 block ${isDarkMode ? "text-white/25" : "text-gray-300"}`}>{timeAgo(n.created_at)}</span>
                        </div>
                        <button
                        onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-white/10 transition-all"
                        data-testid={`notification-delete-${n.id}-button`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-white/30" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
